import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import { dateFormats } from 'commons/src/constants';
import { BuildingType, DayUsageHours } from 'commons/src/models/commonTypeScript';
import { PDFContext } from '../../../../../models/common';
import iaqInsight2PdfStyles from '../utils/pdfStyles';

type DayScheduleProps = {
    usageHours: { [day: string]: DayUsageHours };
    translationFunction: PDFContext;
    dateFormat: keyof typeof dateFormats;
};

const defaultOpeningHoursForAllDays = {
    monday: { closed: false },
    tuesday: { closed: false },
    wednesday: { closed: false },
    thursday: { closed: false },
    friday: { closed: false },
    saturday: { closed: false },
    sunday: { closed: false },
};

export const dayLabelKeys = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DaySchedule = ({ usageHours, translationFunction, dateFormat }: DayScheduleProps): React.ReactElement => {
    const mergedUsageHoursWithDefaults: { [dayKey: string]: DayUsageHours } = {
        ...defaultOpeningHoursForAllDays,
        ...usageHours,
    };
    const usageHoursAsArrayWithLabels = Object.keys(mergedUsageHoursWithDefaults).map((dayKey, dayIndexInWeek) => {
        const fromTimeFormatted = mergedUsageHoursWithDefaults[dayKey]?.from
            ? moment(mergedUsageHoursWithDefaults[dayKey].from, 'HH:mm').format(dateFormats[dateFormat].hourFormat)
            : '';
        const toTimeFormatted = mergedUsageHoursWithDefaults[dayKey]?.to
            ? moment(mergedUsageHoursWithDefaults[dayKey].to, 'HH:mm').format(dateFormats[dateFormat].hourFormat)
            : '';
        const openingHoursDisplayValue = mergedUsageHoursWithDefaults[dayKey]?.closed
            ? translationFunction('Building.Closed')
            : `${fromTimeFormatted} - ${toTimeFormatted}`;
        return {
            label: dayLabelKeys[dayIndexInWeek],
            value: openingHoursDisplayValue,
            day: dayKey,
        };
    });
    const usageHoursLinesForDisplay =
        dateFormat === 'US'
            ? [usageHoursAsArrayWithLabels.slice(0, 5), usageHoursAsArrayWithLabels.slice(5)]
            : [usageHoursAsArrayWithLabels];

    return (
        <View style={{ marginBottom: '32' }}>
            <Text style={iaqInsight2PdfStyles.subHeader}>
                {translationFunction('Building.DayScheduleOpeningHours')}
            </Text>
            {usageHoursLinesForDisplay.map((usageHoursLineForDisplay, usageHoursLineIndex) => {
                const usageHoursLineKey = `usageHoursLine${usageHoursLineIndex}`;
                const secondLineStyle = usageHoursLineIndex === 1 ? { marginTop: '2vw', width: '33vw' } : {};
                return (
                    <View key={usageHoursLineKey} style={[iaqInsight2PdfStyles.flexWrapperSpaced, secondLineStyle]}>
                        {usageHoursLineForDisplay.map(dayScheduleItem => (
                            <View key={dayScheduleItem.day} style={iaqInsight2PdfStyles.openingHoursDayItem}>
                                <Text style={iaqInsight2PdfStyles.dayLabel}>
                                    {translationFunction(dayScheduleItem.label)}
                                </Text>
                                <Text style={iaqInsight2PdfStyles.dayValue}>{dayScheduleItem.value}</Text>
                            </View>
                        ))}
                    </View>
                );
            })}
        </View>
    );
};

type IaqInsight2BuildingDetailsProps = {
    building: BuildingType;
    translationFunction: PDFContext;
    dateFormat: keyof typeof dateFormats;
};

const IaqInsight2BuildingDetails = ({
    building,
    dateFormat,
    translationFunction,
}: IaqInsight2BuildingDetailsProps): React.ReactElement => {
    return (
        <View wrap={false}>
            <View wrap={false}>
                <View style={iaqInsight2PdfStyles.buildingDetailsGrid} wrap={false}>
                    <View style={iaqInsight2PdfStyles.buildingDetailItem}>
                        <Text style={iaqInsight2PdfStyles.buildingDetailLabel}>
                            {translationFunction('BuildingType')}
                        </Text>
                        <Text style={iaqInsight2PdfStyles.buildingDetailValue}>
                            {building.buildingType
                                ? translationFunction(`BuildingTypeOptions.${building.buildingType.toString()}`)
                                : '-'}
                        </Text>
                    </View>
                    <View style={iaqInsight2PdfStyles.buildingDetailItem}>
                        <Text style={iaqInsight2PdfStyles.buildingDetailLabel}>
                            {translationFunction('BuildingYear')}
                        </Text>
                        <Text style={iaqInsight2PdfStyles.buildingDetailValue}>
                            {building.buildingYear ? building.buildingYear.toString() : '-'}
                        </Text>
                    </View>
                    <View style={iaqInsight2PdfStyles.buildingDetailItem}>
                        <Text style={iaqInsight2PdfStyles.buildingDetailLabel}>
                            {translationFunction('Ventilation')}
                        </Text>
                        <Text style={iaqInsight2PdfStyles.buildingDetailValue}>
                            {building.ventilationType
                                ? translationFunction(`VentilationTypeOptions.${building.ventilationType.toString()}`)
                                : '-'}
                        </Text>
                    </View>
                    <View style={iaqInsight2PdfStyles.buildingDetailItem}>
                        <Text style={iaqInsight2PdfStyles.buildingDetailLabel}>
                            {translationFunction('NumberOfFloors')}
                        </Text>
                        <Text style={iaqInsight2PdfStyles.buildingDetailValue}>
                            {building.floors ? building.floors.toString() : '-'}
                        </Text>
                    </View>
                </View>
            </View>
            <DaySchedule
                translationFunction={translationFunction}
                usageHours={building.usageHours}
                dateFormat={dateFormat}
            />
        </View>
    );
};

export default IaqInsight2BuildingDetails;
